import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  Redlink,
  RedlinkEvaluation,
  RedlinkEvaluationResponse,
  REDLINK_TARGET_LABEL,
  RedlinkStatus,
  RedlinkTargetType,
} from '../../helpers/redlinkData';
import moment from 'moment';

interface RedlinkListProps {
  onEdit: (redlinkId: number) => void;
  onNew: () => void;
}

const RedlinkList: React.FC<RedlinkListProps> = ({ onEdit, onNew }) => {
  const [redlinks, setRedlinks] = useState<Redlink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RedlinkStatus | ''>('');
  const [targetFilter, setTargetFilter] = useState<RedlinkTargetType | ''>('');
  const [sourceFilter, setSourceFilter] = useState<
    '' | 'ai' | 'ai_heatup' | 'ai_followup' | 'manual'
  >('');
  // Lifetime performance per redlink (ETKP/ANFOs/CPA), merged by id into the list.
  const [evalMap, setEvalMap] = useState<Map<number, RedlinkEvaluation>>(new Map());

  const loadRedlinks = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = '/api/newsletter-redlinks?limit=100';
      if (statusFilter) url += `&status=${statusFilter}`;
      if (targetFilter) url += `&target_type=${targetFilter}`;
      if (sourceFilter) url += `&source=${sourceFilter}`;

      const response = await fetchData<Redlink[]>(url);
      if (response.success && response.data) {
        setRedlinks(response.data);
      } else {
        setError('Fehler beim Laden der Redlinks');
      }
    } catch (err) {
      setError('Fehler beim Laden der Redlinks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRedlinks();
  }, [statusFilter, targetFilter, sourceFilter]);

  // Lifetime performance (independent of the status/target filters — keyed by id).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData<RedlinkEvaluationResponse>(
          '/api/newsletter-redlinks/evaluation/redlinks',
        );
        if (!cancelled && res.success && res.data) {
          setEvalMap(new Map(res.data.redlinks.map((r) => [r.id, r])));
        }
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async (redlinkId: number) => {
    if (!window.confirm('Redlink wirklich löschen?')) return;

    try {
      const response = await fetchData(`/api/newsletter-redlinks/${redlinkId}`, {
        method: 'DELETE',
      });
      if (response.success) {
        loadRedlinks();
      } else {
        alert('Fehler beim Löschen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Löschen');
      console.error(err);
    }
  };

  const getStatusChip = (status: RedlinkStatus) => {
    const colors: Record<RedlinkStatus, 'default' | 'primary' | 'success' | 'warning'> = {
      draft: 'default',
      active: 'success',
      paused: 'warning',
      archived: 'default',
    };
    const labels: Record<RedlinkStatus, string> = {
      draft: 'Entwurf',
      active: 'Aktiv',
      paused: 'Pausiert',
      archived: 'Archiviert',
    };
    return <Chip size="small" label={labels[status]} color={colors[status]} />;
  };

  // Autogen rows carry a target type chosen for ATTRIBUTION (Nachfass = plx for
  // banner=, Heatup = webinar for affiliate=), which says nothing an operator
  // cares about. Show the genre + touch instead.
  const getTargetChip = (redlink: Redlink) => {
    if (redlink.generated_by === 'ai') {
      const heatup = redlink.ai_kind === 'heatup';
      // Heat-ups carry the day offset since 2026-09-15 (per-day fill); older
      // heat-up rows were backfilled from their touch by the migration.
      const label = heatup
        ? `Heatup T-${redlink.ai_days_before ?? '?'}`
        : `Nachfass ${redlink.ai_touch ?? '?'}/2`;
      return (
        <Chip
          size="small"
          color={heatup ? 'warning' : 'info'}
          label={label}
          title={
            heatup
              ? 'KI-generierter Webinar-Heatup-Redlink (Anmeldung) — T-0 = am Webinar-Tag, T-1 = Tag davor, T-2/T-3 = Ankündigung'
              : 'KI-generierter Post-Webinar-Redlink (Aufzeichnung) — Touch 1 = Tag danach, 2 = letzte Chance'
          }
        />
      );
    }
    const color: Record<RedlinkTargetType, 'primary' | 'secondary' | 'success'> = {
      plx: 'primary',
      webinar: 'secondary',
      livestream: 'success',
    };
    return (
      <Chip
        size="small"
        variant="outlined"
        color={color[redlink.target_type]}
        label={REDLINK_TARGET_LABEL[redlink.target_type]}
      />
    );
  };

  // Render a performance cell from the eval map, or an em-dash if not yet sent.
  const evCell = (id: number, render: (ev: RedlinkEvaluation) => string) => {
    const ev = evalMap.get(id);
    return ev ? render(ev) : '–';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as RedlinkStatus | '')}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="draft">Entwurf</MenuItem>
              <MenuItem value="active">Aktiv</MenuItem>
              <MenuItem value="paused">Pausiert</MenuItem>
              <MenuItem value="archived">Archiviert</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Ziel</InputLabel>
            <Select
              value={targetFilter}
              label="Ziel"
              onChange={(e) => setTargetFilter(e.target.value as RedlinkTargetType | '')}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="plx">PLX</MenuItem>
              <MenuItem value="webinar">Webinar</MenuItem>
              <MenuItem value="livestream">Live-Sendung</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Quelle</InputLabel>
            <Select
              value={sourceFilter}
              label="Quelle"
              onChange={(e) =>
                setSourceFilter(
                  e.target.value as '' | 'ai' | 'ai_heatup' | 'ai_followup' | 'manual',
                )
              }
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="manual">Manuell</MenuItem>
              <MenuItem value="ai">KI (alle)</MenuItem>
              <MenuItem value="ai_heatup">KI-Heatup</MenuItem>
              <MenuItem value="ai_followup">KI-Nachfass</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onNew}>
          Neuer Redlink
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                ID
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Headline
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Ziel
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Produkt/Webinar
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Concept
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                WKZ
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                ANFOs
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                CPA-Umsatz
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                ETKP
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Status
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Geplant
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Erstellt
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redlinks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  Keine Redlinks gefunden
                </TableCell>
              </TableRow>
            ) : (
              redlinks.map((redlink) => (
                <TableRow key={redlink.id} hover>
                  <TableCell>{redlink.id}</TableCell>
                  <TableCell>{redlink.headline}</TableCell>
                  <TableCell>{getTargetChip(redlink)}</TableCell>
                  <TableCell>
                    {/* Livestream zuerst: die Zeile darf ein optionales Produkt
                        tragen (nur Beschriftung) — ohne diesen Zweig stünde dort
                        "Produkt #5", als wäre das Produkt das Ziel der Anzeige. */}
                    {redlink.target_type === 'livestream'
                      ? redlink.scheduled_date
                        ? `Sendung ${moment(redlink.scheduled_date).format('DD.MM.YYYY')}`
                        : 'Live-Sendung'
                      : redlink.webinar_id
                        ? redlink.webinar_id
                        : redlink.product_id
                          ? `Produkt #${redlink.product_id}`
                          : '-'}
                  </TableCell>
                  <TableCell>{redlink.concept || '-'}</TableCell>
                  <TableCell>{redlink.wkz_code || '-'}</TableCell>
                  <TableCell align="right">
                    {evCell(redlink.id, (ev) => formatNumber(ev.totalAnfos, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {evCell(redlink.id, (ev) => `${formatNumber(ev.totalCpaRevenue, 2)} €`)}
                  </TableCell>
                  <TableCell align="right">
                    {evCell(redlink.id, (ev) => `${formatNumber(ev.cpaEtkp, 2)} €`)}
                  </TableCell>
                  <TableCell>{getStatusChip(redlink.status)}</TableCell>
                  <TableCell>
                    {redlink.scheduled_date
                      ? moment(redlink.scheduled_date).format('DD.MM.YYYY')
                      : '-'}
                  </TableCell>
                  <TableCell>{moment(redlink.created_at).format('DD.MM.YYYY')}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onEdit(redlink.id)} title="Bearbeiten">
                      <EditIcon />
                    </IconButton>
                    {redlink.status === 'draft' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(redlink.id)}
                        title="Löschen"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RedlinkList;
